import { memo } from 'react'
import type { ToolMessage } from '../../../../types'
import { FaviconItem } from '../favicon-item'
import { faviconUrlFor } from '../../../../utils/favicon-url'
import { resolveToolSubmessageEntry } from './registry'

/**
 * Renders one expanded `role: 'tool'` submessage. Named tools resolve their
 * renderer through the tools registry; Exa search results carry no tool name
 * and are recognized by the shape of their content instead.
 */
export const ToolSubmessageRenderer = memo(function ToolSubmessageRenderer({
  message,
  onClickSearchResult,
}: {
  /** The caller narrows `content` to string before dispatching here. */
  message: ToolMessage & { content: string }
  onClickSearchResult: (url: string) => void
}) {
  const content = message.content
  const name = message.name

  // Exa search results (favicon-based)
  if (content.includes('favicon')) {
    try {
      const searchResults = JSON.parse(content)
      return (
        <div className="-mt-2 flex flex-wrap gap-2">
          {searchResults?.results?.slice(0, 5)?.map((result: any, index: number) => (
            <button
              key={`${message.id}-${index}`}
              onClick={() => onClickSearchResult(result.url)}
              className="bg-light-10 hover:bg-light-15 flex h-7 w-fit cursor-pointer items-center gap-1 rounded-full px-1.5 transition-colors"
            >
              <FaviconItem src={result.favicon || faviconUrlFor(result.url)} className="h-4 w-4" />
              <span className="text-secondary-old truncate text-sm font-normal">{hostnameOf(result.url)}</span>
            </button>
          ))}
        </div>
      )
    } catch {
      return null
    }
  }

  const entry = resolveToolSubmessageEntry(name)
  if (entry?.submessage) {
    let result: any
    try {
      result = JSON.parse(content)
    } catch {
      return null
    }
    return <div className="mt-2">{entry.submessage({ result })}</div>
  }

  // Unrecognized tool message — don't render
  return null
})

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname
  } catch {
    return url
  }
}
